/** Native work can outlive a surface; its callbacks must not keep that owner. */
export type PaintTaskOutcome = 'fulfilled' | 'rejected' | 'cancelled'

export type PaintTaskLease = {
  promise: Promise<PaintTaskOutcome>
  cancel: () => void
}

/** The subset of a native image required to own its readiness wait. */
export type ImageLike = {
  complete: boolean
  naturalWidth: number
  decode: () => Promise<unknown>
  addEventListener: (
    type: string,
    listener: () => void,
    options?: { once?: boolean }
  ) => void
  removeEventListener: (type: string, listener: () => void) => void
}

export type ImagePaintLease = {
  promise: Promise<void>
  cancel: () => void
}

export type PaintFrameLease = { cancel: () => void }

class NativePaintTaskLease implements PaintTaskLease {
  readonly promise: Promise<PaintTaskOutcome>
  private resolve: ((outcome: PaintTaskOutcome) => void) | null = null

  constructor(task: Promise<unknown>) {
    this.promise = new Promise((resolve) => {
      this.resolve = resolve
    })
    // Only this module-owned lease is reachable from an unabortable native
    // promise. Cancellation clears its resolver and settles the local wait.
    void task.then(
      this.finish.bind(this, 'fulfilled'),
      this.finish.bind(this, 'rejected')
    )
  }

  cancel(): void {
    this.finish('cancelled')
  }

  private finish(outcome: PaintTaskOutcome): void {
    const resolve = this.resolve
    this.resolve = null
    resolve?.(outcome)
  }
}

const pendingImageDecodes = new Set<NativeImagePaintLease>()
let activeImageDecode: NativeImagePaintLease | null = null

function retireImageDecode(): void {
  activeImageDecode = null
  // Queued images can finish loading without decode(). Avoid a synchronous
  // chain through all those complete images when the current decode settles.
  queueMicrotask(pumpImageDecodes)
}

function pumpImageDecodes(): void {
  if (activeImageDecode) {
    return
  }
  const next = pendingImageDecodes.values().next()
  if (next.done) {
    return
  }
  pendingImageDecodes.delete(next.value)
  activeImageDecode = next.value
  next.value.startDecode(retireImageDecode)
}

class NativeImagePaintLease implements ImagePaintLease {
  readonly promise: Promise<void>
  private image: ImageLike | null
  private resolve: (() => void) | null = null
  private readonly onImageEvent = this.finish.bind(this)

  constructor(image: ImageLike) {
    this.image = image
    this.promise = new Promise((resolve) => {
      this.resolve = resolve
    })
    if (image.complete && image.naturalWidth > 0) {
      this.finish()
      return
    }
    pendingImageDecodes.add(this)
    pumpImageDecodes()
  }

  startDecode(retire: () => void): void {
    const image = this.image
    if (!image || !this.resolve || (image.complete && image.naturalWidth > 0)) {
      this.finish()
      retire()
      return
    }
    let decode: Promise<unknown>
    try {
      decode = image.decode()
    } catch (error) {
      decode = Promise.reject(error)
    }
    // Cancellation releases this lease's DOM references, but native work owns
    // its admission until the original decode promise actually settles.
    void decode.then(
      this.onNativeSettled.bind(this, 'fulfilled', retire),
      this.onNativeSettled.bind(this, 'rejected', retire)
    )
  }

  cancel(): void {
    this.finish()
  }

  private onNativeSettled(
    outcome: Exclude<PaintTaskOutcome, 'cancelled'>,
    retire: () => void
  ): void {
    try {
      this.onDecodeSettled(outcome)
    } finally {
      retire()
    }
  }

  private onDecodeSettled(outcome: PaintTaskOutcome): void {
    const image = this.image
    if (!image || !this.resolve) {
      return
    }
    if (outcome !== 'rejected' || image.complete) {
      this.finish()
      return
    }
    image.addEventListener('load', this.onImageEvent, { once: true })
    image.addEventListener('error', this.onImageEvent, { once: true })
  }

  private finish(): void {
    const image = this.image
    const resolve = this.resolve
    pendingImageDecodes.delete(this)
    this.image = null
    this.resolve = null
    image?.removeEventListener('load', this.onImageEvent)
    image?.removeEventListener('error', this.onImageEvent)
    resolve?.()
  }
}

class NativePaintFrameLease implements PaintFrameLease {
  private callback: (() => void) | null

  constructor(raf: (callback: () => void) => number, callback: () => void) {
    this.callback = callback
    raf(this.run.bind(this))
  }

  cancel(): void {
    this.callback = null
  }

  private run(): void {
    const callback = this.callback
    this.callback = null
    callback?.()
  }
}

export function createPaintTaskLease(task: Promise<unknown>): PaintTaskLease {
  return new NativePaintTaskLease(task)
}

/** Cancellation drops a waiter; only native completion releases decode admission. */
export function createImagePaintLease(image: ImageLike): ImagePaintLease {
  return new NativeImagePaintLease(image)
}

/** Preserve nested rAF ordering while releasing a cancelled host callback. */
export function createPaintFrameLease(
  raf: (callback: () => void) => number,
  callback: () => void
): PaintFrameLease {
  return new NativePaintFrameLease(raf, callback)
}
